import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

import { environment } from '../../environments/environment';

/**
 * Loads the Google Maps JavaScript API once per page, on demand.
 *
 * `@angular/google-maps` does NOT inject the script tag for you — by design,
 * because you usually want to control libraries (places, geometry), the key,
 * and the loading strategy. We load `geometry` because the side panel needs
 * `google.maps.geometry.spherical.computeDistanceBetween` to sort by distance.
 *
 * The first call kicks off loading and returns an Observable that completes
 * when the API is ready. Subsequent calls return immediately from cache.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private ready$ = new ReplaySubject<boolean>(1);
  private loadStarted = false;

  load(): Observable<boolean> {
    if (this.loadStarted) {
      return this.ready$.asObservable();
    }
    this.loadStarted = true;

    const apiKey = (environment as any).googleMapsApiKey || '';
    if (!apiKey) {
      // Surface a clear console message; the page still renders a degraded UI.
      // tslint:disable-next-line:no-console
      console.warn(
        '[JobMap] googleMapsApiKey is empty in src/environments/environment.ts — map will not load.',
      );
      this.ready$.next(false);
      this.ready$.complete();
      return this.ready$.asObservable();
    }

    // If a previous module already injected the script, just resolve.
    if (typeof window !== 'undefined' && (window as any).google && (window as any).google.maps) {
      this.ready$.next(true);
      this.ready$.complete();
      return this.ready$.asObservable();
    }

    const script = document.createElement('script');
    // NOTE: do NOT add `&loading=async`.
    //
    // Google's `loading=async` mode requires consumers to call
    // `await google.maps.importLibrary("maps")` to get constructors.
    // @angular/google-maps 14.x predates that change — it calls
    // `new google.maps.Map(...)` synchronously when the <google-map>
    // element mounts, which silently fails under async loading and
    // produces a zero-height blank map.
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(apiKey)}` +
      '&libraries=geometry';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.ready$.next(true);
      this.ready$.complete();
    };
    script.onerror = () => {
      // tslint:disable-next-line:no-console
      console.error('[JobMap] Failed to load Google Maps JS API.');
      this.ready$.next(false);
      this.ready$.complete();
    };
    document.head.appendChild(script);

    return this.ready$.asObservable();
  }
}
